import { EventType, MatrixEvent, RoomEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { RoomAccessType } from 'hooks/roomCreation/useRoomAccessInitialState';
import useBotValidatedAction, { BotValidatedState } from 'hooks/useBotValidatedAction/useBotValidatedAction';
import { CustomMeedioMatrixEvents, MEEDIO_BOT_ID } from 'utils/matrixUtils';

export const ROOM_ACCESS_CHANGE_TIMEOUT_MS = 12_000;

const joinRuleByAccessType: Record<RoomAccessType, string> = {
  team: 'restricted',
  shared: 'public',
  knock: 'knock',
};

export interface UseRoomAccessChangeReturn {
  state: BotValidatedState;
  changeAccess: (accessType: RoomAccessType) => Promise<void>;
  reset: () => void;
}

const useRoomAccessChange = (roomId: string): UseRoomAccessChangeReturn => {
  const { matrixClient } = useMatrixContext();

  const chosenAccessTypeRef = useRef<RoomAccessType | null>(null);
  const timelineHandlerRef = useRef<((event: MatrixEvent) => void) | null>(null);

  const unsubscribeTimeline = useCallback(() => {
    if (timelineHandlerRef.current !== null) {
      matrixClient.off(RoomEvent.Timeline, timelineHandlerRef.current);
      timelineHandlerRef.current = null;
    }
  }, [matrixClient]);

  const action = useCallback(async () => {
    const chosen = chosenAccessTypeRef.current;
    if (!chosen) throw new Error('useRoomAccessChange: no access type chosen');

    await matrixClient.sendStateEvent(roomId, CustomMeedioMatrixEvents.ROOM_ACCESS, { type: chosen });

    const expectedJoinRule = joinRuleByAccessType[chosen];

    return new Promise<void>((resolve, reject) => {
      const handler = (event: MatrixEvent) => {
        if (event.getRoomId() !== roomId) return;

        const eventType = event.getType();

        if (eventType === EventType.RoomJoinRules) {
          const observedJoinRule = (event.getContent() as { join_rule?: string }).join_rule;
          if (observedJoinRule === expectedJoinRule) {
            resolve();
          }
          return;
        }

        if (
          eventType === CustomMeedioMatrixEvents.ROOM_ACCESS &&
          MEEDIO_BOT_ID !== undefined &&
          event.getSender() === MEEDIO_BOT_ID
        ) {
          const observedAccessType = (event.getContent() as { type?: string }).type;
          if (observedAccessType !== chosen) {
            reject(new Error('useRoomAccessChange: access reverted by bot'));
          }
        }
      };

      timelineHandlerRef.current = handler;
      matrixClient.on(RoomEvent.Timeline, handler);
    });
  }, [matrixClient, roomId]);

  const { state, execute, reset } = useBotValidatedAction<void>({
    action,
    onSuccess: unsubscribeTimeline,
    onReject: unsubscribeTimeline,
    timeoutMs: ROOM_ACCESS_CHANGE_TIMEOUT_MS,
    timeoutIsSuccess: false,
  });

  useEffect(() => {
    if (state !== 'pending') unsubscribeTimeline();
  }, [state, unsubscribeTimeline]);

  useEffect(() => () => unsubscribeTimeline(), [unsubscribeTimeline]);

  const changeAccess = useCallback(
    async (accessType: RoomAccessType) => {
      chosenAccessTypeRef.current = accessType;
      await execute();
    },
    [execute]
  );

  return { state, changeAccess, reset };
};

export type { BotValidatedState, RoomAccessType };

export default useRoomAccessChange;
